import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import { mdiDownload } from '@mdi/js';

import { EnrollmentsManagementTableRow } from 'offer/components/EnrollmentsManagementTable';
import { FilterBar } from 'common/components/FilterBar';
import {
    useEnrollmentsManagementList,
    useEnrollmentsManagementMutations,
} from 'offer/hooks/enrollment';
import { TFilterFormValues } from 'common/types';
import {
    IOfferEventDate,
    IOfferEventEnrollmentSortField,
    IOfferEventSubscriptionStatus,
    IFeature,
    ExportOfferEventEnrollmentDocument,
} from 'graphql/types';
import {
    TableActionBar,
    TableHead,
    TableCell,
    TableSortButton,
    useSelection,
} from 'common/components/Table';
import { Checkbox } from 'common/components/Checkbox';
import { Loader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import {
    EnrollmentStatusDialog,
    TEnrollmentStatusDialogSettings,
} from 'offer/components/EnrollmentStatusDialog';
import {
    EnrollmentDeleteDialog,
    TEnrollmentDeleteDialogSettings,
} from 'offer/components/EnrollmentDeleteDialog';
import { EnrollmentTableHeadActions } from 'offer/components/EnrollmentTableHeadActions';
import { EnrollmentBulkMailDialog } from 'offer/components/EnrollmentBulkMailDialog';
import { TEnrollmentBulkMailDialogSettings } from 'offer/components/EnrollmentBulkMailDialog/EnrollmentBulkMailDialog';
import { EnrollmentAttendanceDialog } from 'offer/components/EnrollmentAttendanceDialog';
import { CreateEnrollmentCertificateDialog } from 'common/components/CreateEnrollmentCertificateDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { useFeature } from 'common/hooks/useFeature';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

interface IProps {
    enrollmentStatus: IOfferEventSubscriptionStatus;
}

export const EnrollmentsManagementTable = ({ enrollmentStatus }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const { canUse: canExport } = useFeature(IFeature.Export);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [enrollmentStatusDialogSettings, setEnrollmentStatusDialogSettings] =
        useState<TEnrollmentStatusDialogSettings>({
            open: false,
            selectedAmount: 0,
        });
    const [enrollmentDeleteDialogSettings, setEnrollmentDeleteDialogSettings] =
        useState<TEnrollmentDeleteDialogSettings>({
            open: false,
            selectedAmount: 0,
        });
    const [bulkMailDialogSettings, setBulkEmailDialogSettings] =
        useState<TEnrollmentBulkMailDialogSettings>({
            open: false,
            selectedAmount: 0,
        });
    const [
        enrollmentAttendanceDialogSettings,
        setEnrollmentAttendanceDialogSettings,
    ] = useState<{
        open: boolean;
        enrollmentId?: string;
    }>({
        open: false,
    });
    const [
        createCertificateDialogSettings,
        setCreateCertificateDialogSettings,
    ] = useState<{ enrollmentIds?: string[]; open: boolean }>({
        enrollmentIds: undefined,
        open: false,
    });

    const {
        enrollments,
        loading: loadingEnrollments,
        currentSort,
        filters,
        filterValues,
        exportQueryVariables,
        paginationSettings,
        onSearch,
        onFilterChange,
        onSort,
        setPage,
    } = useEnrollmentsManagementList(enrollmentStatus);

    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportOfferEventEnrollmentDocument,
        {
            variables: exportQueryVariables,
        }
    );

    const { ...selectionProps } = useSelection(enrollments);
    const { rows, selected, onSelectAll, isSelected } = selectionProps;

    const {
        onUpdateEnrollments,
        onDeleteEnrollments,
        loading: enrollmentMutationsLoading,
    } = useEnrollmentsManagementMutations();

    const deselectAllEnrollments = () => {
        if (!!selected?.length) onSelectAll?.();
    };

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);

        deselectAllEnrollments();
    };

    const handleFilterChange = (values?: TFilterFormValues) => {
        setPage(1);
        onFilterChange?.(values);

        deselectAllEnrollments();
    };

    const handleEnrollmentStatusDialog = (
        open: boolean,
        selectedAmount: number,
        status: IOfferEventSubscriptionStatus,
        enrollmentId?: string
    ) => {
        setEnrollmentStatusDialogSettings({
            open,
            status,
            enrollmentId,
            selectedAmount,
        });
    };

    const handleExport = async () => {
        try {
            const exportResults = await exportReport();
            const fileUrl =
                exportResults.data?.exportOfferEventEnrollment?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(
                    translate('enrollmentsManagementTable.export.success'),
                    {
                        variant: 'success',
                    }
                );
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    useEffect(() => {
        deselectAllEnrollments();
    }, [enrollmentStatus]);

    const selectedAmount = selected?.length || 0;

    const isEnrolledStatus =
        enrollmentStatus === IOfferEventSubscriptionStatus.Enrolled;
    const isDeniedStatus =
        enrollmentStatus === IOfferEventSubscriptionStatus.Denied;

    const selectedEnrollmentIds = useMemo(
        () => createCertificateDialogSettings.enrollmentIds || selected || [],
        [createCertificateDialogSettings.enrollmentIds, selected]
    );
    const hasExternalCertificates = useMemo(
        () =>
            enrollments
                .filter((e) => selectedEnrollmentIds.includes(e.id))
                .some((e) => e.offerEvent?.certificate?.isExternalCertificate),
        [enrollments, selectedEnrollmentIds]
    );

    const allSelectedAreExternal = useMemo(
        () =>
            !!selectedEnrollmentIds.length &&
            enrollments
                .filter((e) => selectedEnrollmentIds.includes(e.id))
                .every((e) => e.offerEvent?.certificate?.isExternalCertificate),
        [enrollments, selectedEnrollmentIds]
    );

    const tableActions = (
        <EnrollmentTableHeadActions
            isWebinarOrMeeting
            hide={!selectedAmount}
            onClickCertificate={
                isEnrolledStatus && !allSelectedAreExternal
                    ? () => {
                          setCreateCertificateDialogSettings({
                              enrollmentIds: selected || [],
                              open: true,
                          });
                      }
                    : undefined
            }
            onClickDelete={() => {
                setEnrollmentDeleteDialogSettings({
                    open: true,
                    selectedAmount,
                });
            }}
            onClickEnroll={
                !isEnrolledStatus
                    ? () => {
                          handleEnrollmentStatusDialog(
                              true,
                              selectedAmount,
                              IOfferEventSubscriptionStatus.Enrolled
                          );
                      }
                    : undefined
            }
            onClickMail={() => {
                setBulkEmailDialogSettings({
                    ...enrollmentDeleteDialogSettings,
                    enrollmentId: undefined,
                    open: true,
                    selectedAmount,
                });
            }}
            onClickReject={
                !isDeniedStatus
                    ? () => {
                          handleEnrollmentStatusDialog(
                              true,
                              selectedAmount,
                              IOfferEventSubscriptionStatus.Denied
                          );
                      }
                    : undefined
            }
        />
    );

    const selectedEnrollment = enrollments.find(
        (enrollment) =>
            enrollment.id === enrollmentAttendanceDialogSettings.enrollmentId
    );
    const selectedEnrollmentDates =
        (selectedEnrollment?.offerEvent.dates.filter(
            Boolean
        ) as IOfferEventDate[]) || [];

    return (
        <>
            <FilterBar
                useFilterDrawer
                barAppend={
                    canExport && (
                        <Button
                            disabled={!enrollments.length}
                            startIcon={
                                <Icon path={mdiDownload} size="1.9rem" />
                            }
                            sx={{ flex: 1 }}
                            variant="outlined"
                            onClick={() => setShowExportDialog(true)}
                        />
                    )
                }
                disabled={loadingEnrollments}
                filterDrawerProps={{
                    title: translate(
                        'enrollmentsManagementTable.filterDrawerTitle'
                    ),
                    description: translate(
                        'enrollmentsManagementTable.filterDrawerDescription'
                    ),
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);
                    handleFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loadingEnrollments && !!rows.length && (
                <TableContainer>
                    <TableActionBar
                        actions={tableActions}
                        selectionProps={selectionProps}
                    />
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={false}
                                        disabled={isSelected}
                                        onChange={onSelectAll}
                                    />
                                </TableCell>
                                <TableCell>
                                    {translate(
                                        'enrollmentsManagementTable.name'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {translate(
                                        'enrollmentsManagementTable.meeting'
                                    )}
                                </TableCell>
                                {isDeniedStatus && (
                                    <TableCell>
                                        {translate(
                                            'enrollmentsManagementTable.reason'
                                        )}
                                    </TableCell>
                                )}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate(
                                            'enrollmentsManagementTable.lastUpdated'
                                        )}

                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IOfferEventEnrollmentSortField.StatusUpdatedOn
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IOfferEventEnrollmentSortField.StatusUpdatedOn
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                {isEnrolledStatus && (
                                    <TableCell>
                                        {translate(
                                            'enrollmentsManagementTable.attendance'
                                        )}
                                    </TableCell>
                                )}
                                <TableCell />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows?.map(({ index, onSelect, isSelected }) => {
                                const enrollment = enrollments[index] || null;

                                if (!enrollment) return null;

                                const hasExternalCertificate =
                                    !!enrollment.offerEvent?.certificate
                                        ?.isExternalCertificate;

                                return (
                                    <EnrollmentsManagementTableRow
                                        enrollment={enrollment}
                                        enrollmentStatus={enrollmentStatus}
                                        isSelected={isSelected}
                                        key={enrollment.id}
                                        onClickCertificate={
                                            // Do not pass the function when external certificate to hide the certificate icon
                                            hasExternalCertificate
                                                ? undefined
                                                : () => {
                                                      setCreateCertificateDialogSettings(
                                                          {
                                                              enrollmentIds: [
                                                                  enrollment.id,
                                                              ],
                                                              open: true,
                                                          }
                                                      );
                                                  }
                                        }
                                        onClickComplete={() => {
                                            setEnrollmentAttendanceDialogSettings(
                                                {
                                                    open: true,
                                                    enrollmentId: enrollment.id,
                                                }
                                            );
                                        }}
                                        onClickDelete={() => {
                                            setEnrollmentDeleteDialogSettings({
                                                open: true,
                                                selectedAmount: 1,
                                                enrollmentId: enrollment.id,
                                            });
                                        }}
                                        onClickEnroll={() => {
                                            handleEnrollmentStatusDialog(
                                                true,
                                                1,
                                                IOfferEventSubscriptionStatus.Enrolled,
                                                enrollment.id
                                            );
                                        }}
                                        onClickMail={() => {
                                            setBulkEmailDialogSettings({
                                                open: true,
                                                selectedAmount: 1,
                                                enrollmentId: enrollment.id,
                                            });
                                        }}
                                        onClickReject={() => {
                                            handleEnrollmentStatusDialog(
                                                true,
                                                1,
                                                IOfferEventSubscriptionStatus.Denied,
                                                enrollment.id
                                            );
                                        }}
                                        onSelect={onSelect}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loadingEnrollments && (
                <Box sx={{ position: 'relative', height: '100px', mt: 4 }}>
                    <Loader />
                </Box>
            )}

            {!loadingEnrollments && !enrollments.length && (
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Typography>
                        {translate('noOptionsText.enrollments')}
                    </Typography>
                </Box>
            )}

            {!loadingEnrollments && paginationSettings.count !== -1 && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate('subscription', {
                            count: paginationSettings.count,
                        })}
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}

            <EnrollmentStatusDialog
                dialogSettings={enrollmentStatusDialogSettings}
                loading={enrollmentMutationsLoading}
                showCertificateField={
                    enrollmentStatusDialogSettings.status ===
                    IOfferEventSubscriptionStatus.Completed
                }
                onClose={() =>
                    setEnrollmentStatusDialogSettings({
                        ...enrollmentStatusDialogSettings,
                        enrollmentId: undefined,
                        open: false,
                    })
                }
                onConfirm={async (reason, generateCertificate) => {
                    if (!enrollmentStatusDialogSettings.status) return;

                    const { enrollmentId } = enrollmentStatusDialogSettings;

                    const enrollmentIds = enrollmentId
                        ? [enrollmentId]
                        : selected;

                    await onUpdateEnrollments(
                        enrollmentIds,
                        enrollmentStatusDialogSettings.status,
                        reason,
                        generateCertificate
                    );

                    setEnrollmentStatusDialogSettings({
                        ...enrollmentStatusDialogSettings,
                        enrollmentId: undefined,
                        open: false,
                    });

                    deselectAllEnrollments();
                }}
            />

            <EnrollmentDeleteDialog
                dialogSettings={enrollmentDeleteDialogSettings}
                loading={enrollmentMutationsLoading}
                onClose={() =>
                    setEnrollmentDeleteDialogSettings({
                        ...enrollmentDeleteDialogSettings,
                        open: false,
                    })
                }
                onConfirm={async () => {
                    const { enrollmentId } = enrollmentDeleteDialogSettings;

                    const userIds = enrollmentId ? [enrollmentId] : selected;

                    await onDeleteEnrollments(userIds);

                    setEnrollmentDeleteDialogSettings({
                        ...enrollmentDeleteDialogSettings,
                        enrollmentId: undefined,
                        open: false,
                    });

                    deselectAllEnrollments();
                }}
            />

            <EnrollmentBulkMailDialog
                dialogSettings={bulkMailDialogSettings}
                enrollmentIds={
                    bulkMailDialogSettings.enrollmentId
                        ? [bulkMailDialogSettings.enrollmentId]
                        : selected
                }
                onClose={() => {
                    setBulkEmailDialogSettings({
                        ...enrollmentDeleteDialogSettings,
                        enrollmentId: undefined,
                        open: false,
                    });
                }}
                onSendBulkEmailComplete={() => {
                    deselectAllEnrollments();

                    setBulkEmailDialogSettings({
                        ...enrollmentDeleteDialogSettings,
                        enrollmentId: undefined,
                        open: false,
                    });
                }}
            />

            {isEnrolledStatus && !!selectedEnrollment?.offerEvent.id && (
                <EnrollmentAttendanceDialog
                    dates={selectedEnrollmentDates}
                    enrollments={selectedEnrollment ? [selectedEnrollment] : []}
                    offerEventId={selectedEnrollment.offerEvent.id}
                    open={enrollmentAttendanceDialogSettings.open}
                    onClose={() =>
                        setEnrollmentAttendanceDialogSettings({
                            open: false,
                            enrollmentId: undefined,
                        })
                    }
                    onSubmit={() =>
                        setEnrollmentAttendanceDialogSettings({
                            open: false,
                            enrollmentId: undefined,
                        })
                    }
                />
            )}

            {isEnrolledStatus && (
                <CreateEnrollmentCertificateDialog
                    enrollmentIds={selectedEnrollmentIds}
                    hasExternalCertificates={hasExternalCertificates}
                    open={createCertificateDialogSettings.open}
                    onClose={() =>
                        setCreateCertificateDialogSettings({
                            open: false,
                            enrollmentIds: undefined,
                        })
                    }
                    onConfirm={() => {
                        setCreateCertificateDialogSettings({
                            open: false,
                            enrollmentIds: undefined,
                        });

                        deselectAllEnrollments();
                    }}
                />
            )}

            {!!enrollments.length && (
                <ConfirmDialog
                    confirmText={translate('sendExport')}
                    loading={loadingExport}
                    open={showExportDialog}
                    title={translate(
                        `enrollmentsManagementTable.export.dialog.title`
                    )}
                    onCancel={() => setShowExportDialog(false)}
                    onClose={() => setShowExportDialog(false)}
                    onConfirm={handleExport}
                >
                    {translate(`enrollmentsManagementTable.export.dialog.text`)}
                </ConfirmDialog>
            )}
        </>
    );
};
